import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Services = () => {
  const navigate = useNavigate();

  const services = useMemo(
    () => [
      {
        id: 'intelligent-automation-ai',
        title: 'Intelligent Automation & AI',
        description:
          'Custom LLM workflows and orchestration that turn manual bottlenecks into measurable throughput—grounded in your data and governed for production.',
        features: ['LLM & agentic workflow design', 'RAG & enterprise knowledge', 'Guardrails & evaluation'],
        path: '/services/intelligent-automation-ai',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M437.2 403.5L320 215V64h8c13.3 0 24-10.7 24-24V24c0-13.3-10.7-24-24-24H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h8v151L10.8 403.5C-18.5 450.6 15.3 512 70.9 512h306.2c55.7 0 89.4-61.5 60.1-108.5zM137.9 320l48.2-77.6c3.7-5.2 5.8-11.6 5.8-18.4V64h64v160c0 6.9 2.2 13.2 5.8 18.4l48.2 77.6h-172z" />
          </svg>
        ),
      },
      {
        id: 'cybersecurity',
        title: 'Resilient Cybersecurity',
        description:
          'Beyond static perimeters: zero-trust patterns, identity-first access, logging and detection, and hardening so security keeps pace with how you work.',
        features: ['Zero trust & identity', 'Threat detection & SIEM patterns', 'IR readiness & tabletop exercises'],
        path: '/services/cybersecurity',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.8 82.8c22 9.3 38.4 31 38.3 57.2-.5 99.2-41.3 280.7-213.6 363.2-16.7 8-36.1 8-52.8 0C168.6 420.8 128.1 239.3 128 140c0-26.2 16.4-48.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0zm0 66.8V444c74.6-39.3 133.5-172.5 144.8-284H256V66.8z" />
          </svg>
        ),
      },
      {
        id: 'cloud-infrastructure',
        title: 'Cloud 3.0 Solutions',
        description:
          'Migrations, hybrid connectivity, and cost-disciplined operations across AWS and Azure—landing zones, Kubernetes where it fits, FinOps, and reliability.',
        features: ['AWS, Azure & hybrid', 'IaC & GitOps delivery', 'Observability & FinOps'],
        path: '/services/cloud-infrastructure',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 640 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M537.6 226.6c4.1-10.7 6.4-22.4 6.4-34.6 0-53-43-96-96-96-19.7 0-38.1 6-53.3 16.2C367 64.2 315.3 32 256 32c-88.4 0-160 71.6-160 160 0 2.7.1 5.4.2 8.1C40.2 219.8 0 273.2 0 336c0 79.5 64.5 144 144 144h368c70.7 0 128-57.3 128-128 0-61.9-44-113.6-102.4-125.4z" />
          </svg>
        ),
      },
      {
        id: 'managed-it',
        title: 'Managed IT Excellence',
        description:
          '24/7 high-uptime support for the systems your business runs on—monitoring, patching, backups, and service coordination so teams stay on mission.',
        features: ['NOC-style monitoring & alerts', 'Patching & change windows', 'Backup verification & DR alignment'],
        path: '/services/managed-it',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M256 48C141.1 48 48 141.1 48 256v40c0 13.3-10.7 24-24 24S0 309.3 0 296V256C0 114.6 114.6 0 256 0S512 114.6 512 256v144c0 44.2-35.8 80-80 80H80c-44.2 0-80-35.8-80-80V256c0-13.3 10.7-24 24-24s24 10.7 24 24v144c0 17.7 14.3 32 32 32H432c17.7 0 32-14.3 32-32V256c0-114.9-93.1-208-208-208zM144 208h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H144c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32zm112 0h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H256c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32zm112 0h16c17.7 0 32 14.3 32 32V352c0 17.7-14.3 32-32 32H368c-17.7 0-32-14.3-32-32V240c0-17.7 14.3-32 32-32z" />
          </svg>
        ),
      },
      {
        id: 'web-development',
        title: 'Enterprise Web Development',
        description:
          'Secure, scalable web platforms and APIs—performance, accessibility, and maintainability for teams that outgrow templates and shortcuts.',
        features: ['React, Next.js, Vue ecosystems', 'API design & integration', 'Performance & hardening'],
        path: '/services/web-development',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M505.12019,19.09375c-1.18945-5.53125-6.65819-11-12.207-12.1875C460.716,0,435.507,0,410.40747,0,307.17523,0,245.26909,55.20312,199.05238,128H94.83772c-16.34763.01562-35.55658,11.875-42.88664,26.48438L2.51562,253.29688A28.4,28.4,0,0,0,0,264a24.00867,24.00867,0,0,0,24.00582,24H127.81618l-22.47457,22.46875c-11.36521,11.36133-12.99607,32.25781,0,45.25L156.24582,406.625c11.15623,11.1875,32.15619,13.15625,45.27726,0l22.47457-22.46875V488a24.00867,24.00867,0,0,0,24.00581,24,28.55934,28.55934,0,0,0,10.707-2.51562l98.72834-49.39063c14.62888-7.29687,26.50776-26.5,26.50776-42.85937V312.79688c72.59753-46.3125,128.03493-108.40626,128.03493-211.09376C512.07526,76.5,512.07526,51.29688,505.12019,19.09375ZM384.04033,168A40,40,0,1,1,424.05,128,40.02322,40.02322,0,0,1,384.04033,168Z" />
          </svg>
        ),
      },
      {
        id: 'mobile-development',
        title: 'Native & Cross-Platform Mobile',
        description:
          'iOS and Android experiences with native or cross-platform delivery—offline-ready, observable, and aligned with your backend and security model.',
        features: ['React Native & Flutter', 'Store readiness & releases', 'Secure APIs & analytics hooks'],
        path: '/services/mobile-development',
        icon: (
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 384 512" height="1.25em" width="1.25em" xmlns="http://www.w3.org/2000/svg">
            <path d="M80 0C44.7 0 16 28.7 16 64V448c0 35.3 28.7 64 64 64H304c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H80zm80 432h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H160c-8.8 0-16-7.2-16-16s7.2-16 16-16z" />
          </svg>
        ),
      },
    ],
    []
  );

  return (
    <section id="services" className="services">
      <div className="container">
        <div className="section-header">
          <span className="section-badge">Our Services</span>
          <h2 className="section-title">What We Offer</h2>
          <p className="section-description">
            The digital peak—scaled, secured, sustained. We architect intelligent automation, resilient cybersecurity, Cloud 3.0, and managed IT excellence, plus enterprise web and mobile engineering when you need products built to last—including our work on Kidsphere, commerce, cloud control planes, and regulated portals.{' '}
            <Link to="/portfolio" className="section-inline-link">Browse our work</Link>
            .
          </p>
        </div>

        <div className="services-grid">
          {services.map((service) => (
            <article
              key={service.id}
              className="service-card"
              role="button"
              tabIndex={0}
              onClick={() => navigate(service.path)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(service.path);
                }
              }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature) => (
                  <li key={feature}>
                    <CheckIcon />
                    {feature}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
